import { useState } from "react";
import {
  Form as SubmitForm,
  Link,
  useSearchParams,
  useActionData,
  useNavigation,
} from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaSatelliteDish,
  FaServer,
  FaExclamationCircle,
} from "react-icons/fa";
import ZionLogo from "./ZionLogo";
import { PARAMS } from "../util/GlobalConstants";
import isEmail from "validator/lib/isEmail";
import Spinner from "./Spinner";
import "./AuthForm.css";
import "./AuthVariants.css";

import { PASSWORD_POLICY_REGEX } from "../util/PasswordPolicy";

export const EMAIL_KEY = "email";
export const PASSWORD_KEY = "password";

function formatRetryAfter(seconds) {
  if (seconds >= 60) {
    return `${Math.ceil(seconds / 60)} min`;
  }
  return `${seconds} sec`;
}

const fieldVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.08, type: "spring", stiffness: 240, damping: 24 },
  }),
};

function AuthForm({ variant = null }) {
  const data = useActionData();
  const navigation = useNavigation();
  const [t] = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const [searchParams] = useSearchParams();
  const isLogin = searchParams.get(PARAMS.mode.mode) === PARAMS.mode.login;
  const isSubmitting = navigation.state === "submitting";

  // Unique field ids so stacked variant forms don't collide in the DOM
  const uid = variant ? `v${variant.number}` : "main";
  const emailId = `${uid}-${EMAIL_KEY}`;
  const passwordId = `${uid}-${PASSWORD_KEY}`;

  const signupSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("auth-form.invalid-email"))
      .required(t("auth-form.required"))
      .test(
        "is-valid",
        (message) => `${message.path} is invalid`,
        (value) =>
          value ? isEmail(value) : new Yup.ValidationError("Invalid value")
      ),
    password: isLogin
      ? Yup.string().required(t("auth-form.required"))
      : Yup.string()
          .matches(PASSWORD_POLICY_REGEX, t("auth-form.password-requirements"))
          .required(t("auth-form.required")),
  });

  const features = [
    { icon: FaSatelliteDish, label: t("auth-form.brand-feature-1") },
    { icon: FaShieldAlt, label: t("auth-form.brand-feature-2") },
    { icon: FaServer, label: t("auth-form.brand-feature-3") },
  ];

  return (
    <>
      <div className={`auth-stage ${variant ? "auth-stage--compact" : ""}`}>
        <motion.div
          className={`auth-card ${variant ? variant.className : ""}`}
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 140, damping: 20 }}
        >
          {variant && (
            <span className="auth-variant-badge">
              #{String(variant.number).padStart(2, "0")} · {variant.name}
            </span>
          )}
          {/* Branded mission-control hero */}
          <div className="auth-hero">
            <div className="auth-hero__grid" />
            <div className="auth-hero__glow" />
            <div className="auth-hero__content">
              <div className="auth-brand">
                <span className="auth-brand__mark">
                  <ZionLogo size={26} />
                </span>
                <span className="auth-brand__name">ZION</span>
              </div>
              <h2 className="auth-hero__tagline">
                {t("auth-form.brand-tagline")}
              </h2>
              <ul className="auth-features">
                {features.map(({ icon: Icon, label }, i) => (
                  <motion.li
                    key={label}
                    className="auth-feature"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.12 }}
                  >
                    <span className="auth-feature__icon">
                      <Icon />
                    </span>
                    {label}
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Login / register form */}
          <div className="auth-panel">
            <Formik
              initialValues={{ email: "", password: "" }}
              validationSchema={signupSchema}
            >
              {({ values, errors, touched, handleChange, handleBlur }) => (
                <SubmitForm method="post" className="auth-form">
                  <motion.h1
                    className="auth-title"
                    custom={0}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {isLogin ? t("auth-form.welcome") : t("auth-form.register")}
                  </motion.h1>
                  <motion.p
                    className="auth-subtitle"
                    custom={1}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {isLogin
                      ? t("auth-form.welcome-sub")
                      : t("auth-form.register-sub")}
                  </motion.p>

                  {data && data.retryAfter != null && (
                    <div className="auth-alert">
                      <FaExclamationCircle />
                      <span>
                        {t("auth-form.locked-out", {
                          time: formatRetryAfter(data.retryAfter),
                        })}
                      </span>
                    </div>
                  )}
                  {data && data.retryAfter == null && data.errors && (
                    <div className="auth-alert">
                      <FaExclamationCircle />
                      <ul>
                        {Object.values(data.errors).map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data && data.retryAfter == null && data.message && (
                    <div className="auth-alert">
                      <FaExclamationCircle />
                      <span>{data.message}</span>
                    </div>
                  )}

                  <motion.div
                    className="auth-field"
                    custom={2}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <label htmlFor={emailId}>{t("auth-form.email")}</label>
                    <div
                      className={`auth-input ${
                        touched.email && errors.email ? "auth-input--error" : ""
                      }`}
                    >
                      <FaEnvelope className="auth-input__icon" />
                      <input
                        id={emailId}
                        type="email"
                        name={EMAIL_KEY}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        placeholder={t("auth-form.email")}
                        autoComplete="email"
                        required
                      />
                    </div>
                    {touched.email && errors.email && (
                      <span className="auth-error">{errors.email}</span>
                    )}
                  </motion.div>

                  <motion.div
                    className="auth-field"
                    custom={3}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <label htmlFor={passwordId}>
                      {t("auth-form.password")}
                    </label>
                    <div
                      className={`auth-input ${
                        touched.password && errors.password
                          ? "auth-input--error"
                          : ""
                      }`}
                    >
                      <FaLock className="auth-input__icon" />
                      <input
                        id={passwordId}
                        type={showPassword ? "text" : "password"}
                        name={PASSWORD_KEY}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        placeholder={t("auth-form.password")}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="auth-input__toggle"
                        onClick={() => setShowPassword((s) => !s)}
                        tabIndex={-1}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {touched.password && errors.password && (
                      <span className="auth-error">{errors.password}</span>
                    )}
                  </motion.div>

                  <motion.button
                    type="submit"
                    className="auth-submit"
                    custom={4}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    disabled={
                      values.email.length > 0 && values.password.length > 0
                        ? errors.email !== undefined ||
                          errors.password !== undefined ||
                          isSubmitting
                        : true
                    }
                  >
                    {isSubmitting
                      ? t("auth-form.submitting")
                      : isLogin
                      ? t("auth-form.login")
                      : t("auth-form.register")}
                  </motion.button>

                  <motion.div
                    className="auth-switch"
                    custom={5}
                    variants={fieldVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <span>
                      {isLogin
                        ? t("auth-form.no-account")
                        : t("auth-form.have-account")}
                    </span>
                    <Link
                      to={`?${PARAMS.mode.mode}=${
                        isLogin ? PARAMS.mode.signup : PARAMS.mode.login
                      }`}
                      className="auth-link"
                    >
                      {isLogin
                        ? t("auth-form.register")
                        : t("auth-form.login")}
                    </Link>
                  </motion.div>

                  <div className="auth-secure">
                    <FaShieldAlt />
                    {t("auth-form.secure-connection")}
                  </div>
                </SubmitForm>
              )}
            </Formik>
          </div>
        </motion.div>
      </div>
      {isSubmitting && <Spinner />}
    </>
  );
}

export default AuthForm;
